import {
  useGetInvoiceFilesReportStatuses,
  useGetInvoiceReportStatuses,
} from '@/context/services/reportsType/invoiceReports/Edit.service';
import { useGetAllCacheDataWithQueryKey } from '@/features/approving_invoice_reports/components/hooks/useGetAllCacheDataWithQueryKey';
import { BaseOption } from '@/hooks/useTypeToOptions';
import { getPageData } from '@/utils/getPageData';
import { InfiniteData } from '@tanstack/react-query';
import {
  GetApPayeesResponse,
  GetDeptsResponse,
  getGetApPayeesQueryKey,
  getGetDeptsQueryKey,
  getGetInvoiceFilesStatusesQueryKey,
  getGetInvoiceReportStatusesQueryKey,
  getGetOfficeMembersQueryKey,
  GetOfficeMembersResponse,
  InvoiceFilesStatusesResponse,
  InvoiceReportStatusesResponse,
} from 'ap-openapi';
import { AxiosResponse } from 'axios';
import { useCallback } from 'react';

type ApPayeesInfiniteData = InfiniteData<
  AxiosResponse<GetApPayeesResponse, unknown>,
  string | undefined
>;

type DeptsInfiniteData = InfiniteData<
  AxiosResponse<GetDeptsResponse, unknown>,
  number | undefined
>;

type OfficeMembersInfiniteData = InfiniteData<
  AxiosResponse<GetOfficeMembersResponse, unknown>,
  number | undefined
>;

type InvoiceStatuses = AxiosResponse<InvoiceReportStatusesResponse, unknown>;

type InvoiceFilesStatuses = AxiosResponse<
  InvoiceFilesStatusesResponse,
  unknown
>;

const apPayeeQueryKey = getGetApPayeesQueryKey();
const officeMemberQueryKey = getGetOfficeMembersQueryKey();
const invoiceReportStatusQueryKey = getGetInvoiceReportStatusesQueryKey();
const deptQueryKey = getGetDeptsQueryKey();
const invoiceFilesStatusesQueryKey = getGetInvoiceFilesStatusesQueryKey();

export const useValueRetrieval = () => {
  const { getAllQueriesData } = useGetAllCacheDataWithQueryKey();
  useGetInvoiceReportStatuses();
  useGetInvoiceFilesReportStatuses();

  const getApPayeeValue = useCallback(
    (value: string, extraPayeeOptions?: BaseOption[]) => {
      const extraPayees = extraPayeeOptions?.map((payee) => ({
        id: String(payee.value),
        name: payee.label,
      }));

      const allQueriesData = getAllQueriesData<ApPayeesInfiniteData>([
        apPayeeQueryKey[0],
      ]);
      for (const queryData of allQueriesData) {
        const apPayees = getPageData(queryData[1], 'ap_payees');
        const selectedApPayee = [...apPayees, ...(extraPayees ?? [])].find(
          (apPayee) => apPayee.id === value
        );
        if (selectedApPayee) {
          return selectedApPayee.name;
        }
      }
      return value;
    },
    [getAllQueriesData]
  );

  const getDeptValues = useCallback(
    (values: string[], extraDepOptions?: BaseOption[]) => {
      if (!values.length) return [];
      const extraDeps = extraDepOptions?.map((dept) => ({
        id: String(dept.value),
        display_name: dept.label,
      }));
      const allQueriesData = getAllQueriesData<DeptsInfiniteData>([
        deptQueryKey[0],
      ]);

      // Create a map to store all found departments
      const deptMap = new Map();

      // Collect all departments from all queries
      for (const queryData of allQueriesData) {
        const depts = getPageData(queryData[1], 'depts');
        [...depts, ...(extraDeps ?? [])].forEach((dept) => {
          if (dept.id) {
            deptMap.set(dept.id, dept);
          }
        });
      }

      // Map values to either department names or original values if not found
      return values.map((value) => {
        const dept = deptMap.get(value);
        return dept?.display_name ?? value;
      });
    },
    [getAllQueriesData]
  );

  const getOfficeMemberValues = useCallback(
    (values: string[], extraMemberOptions?: BaseOption[]) => {
      if (!values.length) return [];

      const extraMembers = extraMemberOptions?.map((member) => ({
        id: String(member.value),
        name: member.label,
      }));

      const allQueriesData = getAllQueriesData<OfficeMembersInfiniteData>([
        officeMemberQueryKey[0],
      ]);

      // Create a map to store all found members
      const memberMap = new Map();

      // Collect all members from all queries
      for (const queryData of allQueriesData) {
        const officeMembers = getPageData(queryData[1], 'office_members');
        [...officeMembers, ...(extraMembers ?? [])].forEach((member) => {
          if (member.id) {
            memberMap.set(member.id, member);
          }
        });
      }

      // Map values to either member names or original values if not found
      return values.map((value) => {
        const member = memberMap.get(value);
        return member?.name ?? value;
      });
    },
    [getAllQueriesData]
  );

  const getInvoiceStatusValues = useCallback(
    (value: string, invoiceStatusOptions?: BaseOption[]) => {
      const extraInvoiceStatuses = invoiceStatusOptions?.map((status) => ({
        value: String(status.value),
        name: status.label,
      }));

      const allQueriesData = getAllQueriesData<InvoiceStatuses>([
        invoiceReportStatusQueryKey[0],
      ]);
      for (const queryData of allQueriesData) {
        const invoiceStatuses = queryData[1]?.data;
        const selectedInvoiceStatus = [
          ...(invoiceStatuses ?? []),
          ...(extraInvoiceStatuses ?? []),
        ].find((invoiceStatus) => invoiceStatus.value === value);
        if (selectedInvoiceStatus) {
          return selectedInvoiceStatus.name;
        }
      }
      return value;
    },
    [getAllQueriesData]
  );

  const getInvoiceFilesStatusValues = useCallback(
    ({
      value,
      invoiceFilesStatusOptions,
      isHideIfNoLabel,
    }: {
      value: string;
      invoiceFilesStatusOptions?: BaseOption[];
      isHideIfNoLabel?: boolean;
    }) => {
      const extraInvoiceFilesStatuses = invoiceFilesStatusOptions?.map(
        (status) => ({
          value: String(status.value),
          name: status.label,
        })
      );

      const allQueriesData = getAllQueriesData<InvoiceFilesStatuses>([
        invoiceFilesStatusesQueryKey[0],
      ]);
      for (const queryData of allQueriesData) {
        const invoiceFilesStatuses = queryData[1]?.data;
        const selectedInvoiceFilesStatus = [
          ...(invoiceFilesStatuses ?? []),
          ...(extraInvoiceFilesStatuses ?? []),
        ].find((invoiceFilesStatus) => invoiceFilesStatus.value === value);
        if (selectedInvoiceFilesStatus) {
          return selectedInvoiceFilesStatus.name;
        }
      }
      return isHideIfNoLabel ? '' : value;
    },
    [getAllQueriesData]
  );

  return {
    getApPayeeValue,
    getDeptValues,
    getOfficeMemberValues,
    getInvoiceStatusValues,
    getInvoiceFilesStatusValues,
  };
};
